import api from "./axios";

const PUBLIC_LANDING_ENDPOINT =
  process.env.REACT_APP_PUBLIC_LANDING_ENDPOINT || "/public/landing";

const CURRENT_LANDING_SCHEMA_VERSION = 9;
const LANDING_CONTENT_KEYS = Object.freeze([
  "business",
  "navigation",
  "hero",
  "catalog",
  "about",
  "highlightsIntro",
  "highlights",
  "contact",
  "footer",
  "messages",
  "notifications",
  "theme",
  "designTokens",
  "seo",
  "sections",
  "layout",
]);

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasOwn(source, key) {
  return Object.prototype.hasOwnProperty.call(source || {}, key);
}

function asNonNegativeInteger(value, fallback = 0) {
  const number = Number(value);
  return Number.isSafeInteger(number) && number >= 0 ? number : fallback;
}

function isPubliclyVisibleStatus(value) {
  return ["published", "maintenance"].includes(
    String(value || "").trim().toLowerCase()
  );
}

function unwrapTransport(payload) {
  const first = isPlainObject(payload?.data) ? payload.data : payload;
  return isPlainObject(first?.data) ? first.data : first || {};
}

function hasLandingContent(source) {
  if (!isPlainObject(source)) return false;
  return LANDING_CONTENT_KEYS.some((key) => {
    const value = source[key];
    if (Array.isArray(value)) return value.length > 0;
    return isPlainObject(value);
  });
}

function publicEnvelopeCandidates(root) {
  return [root?.landing, root?.siteSettings, root?.settings, root].filter(
    isPlainObject
  );
}

function publicationMetadata(envelope, content, legacy) {
  const schemaVersion = asNonNegativeInteger(
    envelope?.schemaVersion ?? content?.schemaVersion,
    legacy ? 0 : CURRENT_LANDING_SCHEMA_VERSION
  );
  const publishedRevision = asNonNegativeInteger(
    envelope?.publishedRevision ?? content?.publishedRevision,
    0
  );
  const fallbackRevision = asNonNegativeInteger(
    content?.revision ?? envelope?.revision,
    legacy ? 1 : publishedRevision
  );
  const status = String(
    envelope?.publicStatus || content?.publicStatus || (legacy ? "published" : "draft")
  )
    .trim()
    .toLowerCase();

  return {
    schemaVersion,
    publishedRevision,
    revision: publishedRevision || fallbackRevision,
    status,
    updatedAt:
      content?.updatedAt ||
      envelope?.publishedAt ||
      envelope?.updatedAt ||
      null,
  };
}


function withoutVersionedContainers(source) {
  if (!isPlainObject(source)) return {};
  const { draft, published, ...publicContent } = source;
  return publicContent;
}

function publicProjection(content, metadata) {
  return {
    ...(isPlainObject(content) ? content : {}),
    schemaVersion:
      metadata.schemaVersion || CURRENT_LANDING_SCHEMA_VERSION,
    publicStatus: metadata.status,
    publishedRevision: metadata.publishedRevision,
    revision: metadata.revision,
    updatedAt: metadata.updatedAt,
  };
}

/**
 * Adapta las respuestas públicas históricas y versionadas a un único contrato.
 *
 * Contrato actual:
 * - Si existe `published`, esa instantánea es la única fuente permitida.
 * - Si el backend ya devuelve una proyección plana versionada, se respeta sólo
 *   cuando existe una publicación efectiva.
 * - Los payloads planos previos al schema 7 se aceptan temporalmente como legacy.
 *
 * Nunca usa `draft` ni el espejo superior de un documento versionado cuando la
 * publicación todavía no existe.
 */
export function adaptPublicLandingResponse(payload = {}) {
  const root = unwrapTransport(payload);

  if (isPlainObject(root?.publication) && isPlainObject(root?.landing)) {
    const publishedRevision = asNonNegativeInteger(
      root.publication.publishedRevision ?? root.landing.publishedRevision,
      0
    );
    const metadata = {
      schemaVersion: asNonNegativeInteger(
        root.publication.schemaVersion ?? root.landing.schemaVersion,
        root.publication.legacy === true ? 0 : CURRENT_LANDING_SCHEMA_VERSION
      ),
      publishedRevision,
      revision: asNonNegativeInteger(
        root.publication.revision ?? root.landing.revision,
        publishedRevision
      ),
      status: String(
        root.publication.status || root.landing.publicStatus || "draft"
      )
        .trim()
        .toLowerCase(),
      updatedAt:
        root.publication.updatedAt || root.landing.updatedAt || null,
    };
    const available =
      isPubliclyVisibleStatus(metadata.status) &&
      root.publication.available === true &&
      hasLandingContent(root.landing);

    return {
      ok: root?.ok !== false,
      project: root?.project || null,
      access: root?.access || null,
      landing: publicProjection(
        available ? withoutVersionedContainers(root.landing) : {},
        metadata
      ),
      publication: {
        available,
        legacy: root.publication.legacy === true,
        ...metadata,
      },
    };
  }

  const candidates = publicEnvelopeCandidates(root);
  const versionedEnvelope = candidates.find((candidate) =>
    hasOwn(candidate, "published")
  );

  if (versionedEnvelope) {
    const published = isPlainObject(versionedEnvelope.published)
      ? versionedEnvelope.published
      : null;
    const metadata = publicationMetadata(
      versionedEnvelope,
      published || {},
      false
    );
    const available = Boolean(
      isPubliclyVisibleStatus(metadata.status) &&
        published &&
        hasLandingContent(published)
    );

    return {
      ok: root?.ok !== false,
      project: root?.project || null,
      access: root?.access || null,
      landing: publicProjection(available ? published : {}, metadata),
      publication: {
        available,
        legacy: false,
        ...metadata,
      },
    };
  }

  const envelope = candidates[0] || {};
  const schemaVersion = asNonNegativeInteger(envelope.schemaVersion, 0);
  const hasVersionMetadata =
    schemaVersion >= CURRENT_LANDING_SCHEMA_VERSION ||
    hasOwn(envelope, "publishedRevision") ||
    hasOwn(envelope, "draftRevision");

  if (hasVersionMetadata) {
    const metadata = publicationMetadata(envelope, envelope, false);
    const available =
      hasLandingContent(envelope) &&
      isPubliclyVisibleStatus(metadata.status) &&
      metadata.publishedRevision > 0;

    return {
      ok: root?.ok !== false,
      project: root?.project || null,
      access: root?.access || null,
      landing: publicProjection(
        available ? withoutVersionedContainers(envelope) : {},
        metadata
      ),
      publication: {
        available,
        legacy: false,
        ...metadata,
      },
    };
  }

  const legacyContent = candidates.find(hasLandingContent) || envelope;
  const metadata = publicationMetadata(legacyContent, legacyContent, true);
  const available = hasLandingContent(legacyContent);

  return {
    ok: root?.ok !== false,
    project: root?.project || null,
    access: root?.access || null,
    landing: publicProjection(
      available ? withoutVersionedContainers(legacyContent) : {},
      metadata
    ),
    publication: {
      available,
      legacy: true,
      ...metadata,
    },
  };
}

export function adaptSitePreviewResponse(payload = {}) {
  const root = unwrapTransport(payload);
  const landing = isPlainObject(root?.landing) ? root.landing : {};
  const preview = isPlainObject(root?.preview) ? root.preview : {};
  const draftRevision = asNonNegativeInteger(
    preview.draftRevision ?? landing.revision,
    0
  );

  if (root?.ok === false || !hasLandingContent(landing) || draftRevision <= 0) {
    const error = new Error(
      root?.message || "El backend no devolvió un borrador válido para previsualizar."
    );
    error.code = root?.code || "SITE_PREVIEW_RESPONSE_INVALID";
    throw error;
  }

  const expectedRevision = asNonNegativeInteger(
    payload?.requestedDraftRevision,
    0
  );
  if (expectedRevision > 0 && expectedRevision !== draftRevision) {
    const error = new Error(
      "La revisión recibida no coincide con la vista previa solicitada."
    );
    error.code = "SITE_PREVIEW_REVISION_MISMATCH";
    error.status = 409;
    throw error;
  }

  const expectedProjectKey = String(payload?.requestedProjectKey || "")
    .trim()
    .toLowerCase();
  const responseProjectKey = String(root?.project?.slug || "")
    .trim()
    .toLowerCase();
  if (
    expectedProjectKey &&
    (!responseProjectKey || responseProjectKey !== expectedProjectKey)
  ) {
    const error = new Error(
      "La vista previa recibida pertenece a otro proyecto."
    );
    error.code = "SITE_PREVIEW_PROJECT_MISMATCH";
    error.status = 409;
    throw error;
  }

  const publishedRevision = asNonNegativeInteger(preview.publishedRevision, 0);
  const metadata = {
    schemaVersion: asNonNegativeInteger(
      landing.schemaVersion,
      CURRENT_LANDING_SCHEMA_VERSION
    ),
    publishedRevision,
    revision: draftRevision,
    status: "preview",
    updatedAt: landing.updatedAt || null,
  };

  return {
    ok: true,
    project: root?.project || null,
    access: root?.access || null,
    landing: publicProjection(withoutVersionedContainers(landing), metadata),
    publication: {
      available: true,
      legacy: false,
      ...metadata,
    },
    preview: {
      draftRevision,
      publishedRevision,
      publicStatus: String(preview.publicStatus || "draft").trim().toLowerCase(),
      hasUnpublishedChanges:
        preview.hasUnpublishedChanges === true ||
        publishedRevision <= 0 ||
        draftRevision !== publishedRevision,
      expiresAt: preview.expiresAt || null,
    },
  };
}

export async function getSiteDraftPreview(session) {
  const token = String(session?.token || "").trim();
  const endpoint = String(session?.endpoint || "").trim();
  if (!token || !endpoint) {
    const error = new Error("Falta la sesión temporal de vista previa.");
    error.code = "SITE_PREVIEW_SESSION_REQUIRED";
    throw error;
  }

  const response = await api.post(
    endpoint,
    {},
    {
      withCredentials: false,
      headers: {
        Authorization: `Bearer ${token}`,
        "Cache-Control": "no-store",
        Pragma: "no-cache",
      },
    }
  );

  return adaptSitePreviewResponse({
    ...unwrapTransport(response),
    requestedDraftRevision: session?.draftRevision,
    requestedProjectKey: session?.projectKey,
  });
}

export async function getPublicLanding() {
  const response = await api.get(PUBLIC_LANDING_ENDPOINT, {
    headers: { "Cache-Control": "no-cache" },
  });
  return adaptPublicLandingResponse(response);
}
